import { Capacitor } from '@capacitor/core';
import { API_PREFIX, ERROR_CODES, type ApiMeta } from '@saesak/shared';

// 뒤에 `${API_PREFIX}` 를 이어 붙이므로 끝의 슬래시를 털어낸다.
// `https://api.example.com/` 를 그대로 두면 `//api/v1` 이 되어 전부 404 가 난다.
const BASE_URL = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '').replace(
  /\/+$/,
  '',
);

// 네이티브 앱(웹뷰)은 상대 경로가 자기 자신(https://localhost)을 가리켜 반드시 실패한다.
// 빌드에 API_BASE_URL이 빠진 채 배포된 APK를 진단하기 쉽게, 뭉뚱그린 네트워크 오류
// 대신 원인을 그대로 말해 준다.
const NATIVE_WITHOUT_BASE = Capacitor.isNativePlatform() && !BASE_URL;

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.code === ERROR_CODES.UNAUTHORIZED;
  }

  /** §4 구독 게이트에 막힌 경우 — 화면에서 읽기 전용 안내를 띄운다 */
  get isReadOnly(): boolean {
    return this.code === ERROR_CODES.SUBSCRIPTION_READ_ONLY;
  }
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * 토큰 보관 — 웹은 localStorage, Capacitor 웹뷰도 동일하게 동작한다.
 * 리프레시 로테이션(§5.1) 때문에 저장 값을 항상 최신으로 갈아 끼워야 한다.
 */
const STORAGE_KEY = 'saesak.tokens';
const FACILITY_KEY = 'saesak.facilityId';

export const tokenStore = {
  get(): Tokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Tokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens: Tokens): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  },
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(FACILITY_KEY);
  },
};

export const facilityStore = {
  get: (): string | null => localStorage.getItem(FACILITY_KEY),
  set: (id: string): void => localStorage.setItem(FACILITY_KEY, id),
  clear: (): void => localStorage.removeItem(FACILITY_KEY),
};

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/** 동시에 여러 요청이 401을 받아도 리프레시는 한 번만 돌게 묶는다. */
let refreshInFlight: Promise<Tokens | null> | null = null;

async function refreshTokens(): Promise<Tokens | null> {
  const current = tokenStore.get();
  if (!current?.refreshToken) return null;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!response.ok) return null;
      const json = (await response.json()) as { data: Tokens };
      tokenStore.set({ accessToken: json.data.accessToken, refreshToken: json.data.refreshToken });
      return json.data;
    } catch {
      return null;
    } finally {
      // 다음 401에서 다시 시도할 수 있도록 잠금을 풀어 둔다.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** 시설 스코프를 덮어쓸 때 (§4 멀티테넌시) */
  facilityId?: string | null;
  signal?: AbortSignal;
}

export interface ApiResult<T> {
  data: T;
  meta?: ApiMeta;
}

async function send<T>(path: string, options: RequestOptions, retry: boolean): Promise<ApiResult<T>> {
  if (NATIVE_WITHOUT_BASE) {
    throw new ApiError(
      ERROR_CODES.INTERNAL,
      '앱 빌드에 서버 주소가 들어 있지 않아요. API_BASE_URL을 넣고 다시 빌드해 주세요.',
      0,
    );
  }
  const tokens = tokenStore.get();
  const facilityId = options.facilityId ?? facilityStore.get();

  const response = await fetch(`${BASE_URL}${API_PREFIX}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
      ...(facilityId ? { 'X-Facility-Id': facilityId } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    signal: options.signal,
  });

  const json = (await response.json().catch(() => ({}))) as
    | ApiResult<T>
    | { error: { code: string; message: string; details?: unknown } };

  if (!response.ok) {
    const error = 'error' in json ? json.error : undefined;

    // 액세스 토큰 만료는 한 번만 조용히 갱신하고 재시도한다.
    if (response.status === 401 && retry) {
      const refreshed = await refreshTokens();
      if (refreshed) return send<T>(path, options, false);
      tokenStore.clear();
      onUnauthorized?.();
    }

    throw new ApiError(
      error?.code ?? ERROR_CODES.INTERNAL,
      error?.message ?? '문제가 생겼어요. 잠시 후 다시 시도해 주세요.',
      response.status,
      error?.details,
    );
  }

  return json as ApiResult<T>;
}

export function api<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  return send<T>(path, options, true);
}

/**
 * 인증 헤더를 붙여 바이너리(파일)를 받아온다 — JSON 봉투가 아닌 응답용.
 * 인스타 편집기(§5.5)처럼 캔버스에 그릴 원본 바이트가 필요할 때 쓴다.
 * (R2 서명 URL을 fetch로 받으면 CORS에 막히므로 API가 스트림해 준다.)
 */
export async function apiBlob(path: string): Promise<Blob> {
  if (NATIVE_WITHOUT_BASE) {
    throw new ApiError(
      ERROR_CODES.INTERNAL,
      '앱 빌드에 서버 주소가 들어 있지 않아요. API_BASE_URL을 넣고 다시 빌드해 주세요.',
      0,
    );
  }
  const tokens = tokenStore.get();
  const facilityId = facilityStore.get();

  const response = await fetch(`${BASE_URL}${API_PREFIX}${path}`, {
    headers: {
      ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
      ...(facilityId ? { 'X-Facility-Id': facilityId } : {}),
    },
  });
  if (!response.ok) {
    throw new ApiError(ERROR_CODES.INTERNAL, '파일을 불러오지 못했어요.', response.status);
  }
  return response.blob();
}

/** `{ data }`만 필요할 때 쓰는 짧은 형태 */
export async function apiData<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const result = await api<T>(path, options);
  return result.data;
}
